import React, { useState, useMemo } from "react";
import { Container, KPIGrid, KPICard, FileTable, Button } from "./styles";
import { ModalOverlay, ModalContainer, ModalHeader, ModalBody, ModalFooter, ModalButton } from "../ModalShared";
import { GoCheck, GoX, GoRocket, GoLink, GoAlert, GoFile } from "react-icons/go";
import { FiTarget, FiAlertCircle, FiFileText, FiTruck, FiShoppingBag, FiBox, FiBriefcase, FiLink, FiAlertTriangle, FiX } from "react-icons/fi";

// Função para extrair a raiz do CNPJ (primeiros 8 dígitos)
// CNPJ formato: XX.XXX.XXX/YYYY-ZZ → Raiz: XXXXXXXX (8 dígitos)
const getCnpjRoot = (cnpj) => {
  if (!cnpj) return '';
  const onlyNumbers = cnpj.replace(/\D/g, '');
  return onlyNumbers.substring(0, 8); // Primeiros 8 dígitos = raiz
};

const ReviewScreen = ({ filesStats, onConfirm, onCancel, currentProject }) => {
  const [activeTab, setActiveTab] = useState('vinculados');

  // Raiz do CNPJ do projeto
  const projectCnpjRoot = useMemo(() => {
    return getCnpjRoot(currentProject?.cnpj);
  }, [currentProject]);

  // Separar arquivos em vinculados e requer verificação
  const { vinculados, naoVinculados } = useMemo(() => {
    const vinculados = [];
    const naoVinculados = [];

    filesStats.forEach(file => {
      const fileCnpjRoot = getCnpjRoot(file.cnpj);
      
      // Se não temos CNPJ do projeto, todos são considerados vinculados
      // Se a RAIZ do CNPJ do arquivo corresponde à do projeto, é vinculado
      const isVinculado = !projectCnpjRoot || fileCnpjRoot === projectCnpjRoot;
      
      if (isVinculado) {
        vinculados.push({ ...file, status: 'vinculado' });
      } else {
        naoVinculados.push({ ...file, status: 'nao_vinculado', projectCnpjRoot });
      }
    });

    return { vinculados, naoVinculados };
  }, [filesStats, projectCnpjRoot]);

  // Arquivos da aba atual
  const currentFiles = activeTab === 'vinculados' ? vinculados : naoVinculados;

  // Calcular totais (apenas dos vinculados, que serão processados)
  const totals = vinculados.reduce((acc, curr) => ({
    nfe: acc.nfe + (curr.totalNfe || 0),
    cte: acc.cte + (curr.totalCte || 0),
    nfce: acc.nfce + (curr.totalNfce || 0),
    itens: acc.itens + (curr.totalItens || 0),
    participantes: acc.participantes + (curr.totalParticipantes || 0),
  }), { nfe: 0, cte: 0, nfce: 0, itens: 0, participantes: 0 });

  // Empresas únicas (dos vinculados)
  const uniqueCompanies = new Set(vinculados.map(f => f.cnpj).filter(Boolean)).size;

  // Formatar números
  const fmt = (n) => n.toLocaleString('pt-BR');

  // Verificar se pode processar
  const canProcess = vinculados.length > 0;

  const handleConfirm = () => {
    const approvedFileNames = vinculados.map(f => f.fileName);
    onConfirm(approvedFileNames);
  };

  return (
    <ModalOverlay onClick={onCancel}>
      <ModalContainer $maxWidth="520px" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="header-left">
            <div className="header-icon"><FiTarget /></div>
            <div>
              <h2>Pré-visualização do Processamento</h2>
              <p>
                {vinculados.length} arquivo(s) pronto(s)
                {naoVinculados.length > 0 && ` • ${naoVinculados.length} requer(em) verificação`}
              </p>
            </div>
          </div>
          <button className="close-button" onClick={onCancel}>
            <FiX />
          </button>
        </ModalHeader>

        <ModalBody>
          <Container>

      {/* KPIs - Mostram dados dos arquivos vinculados */}
      <KPIGrid>
        <KPICard className="nfe">
          <div className="card-content">
            <div className="thumb"><FiFileText /></div>
            <div className="text">
              <span className="value">{fmt(totals.nfe)}</span>
              <span className="label">Total NF-e</span>
            </div>
          </div>
        </KPICard>
        
        <KPICard className="cte">
          <div className="card-content">
            <div className="thumb"><FiTruck /></div>
            <div className="text">
              <span className="value">{fmt(totals.cte)}</span>
              <span className="label">Total CT-e</span>
            </div>
          </div>
        </KPICard>
        
        <KPICard className="nfce">
          <div className="card-content">
            <div className="thumb"><FiShoppingBag /></div>
            <div className="text">
              <span className="value">{fmt(totals.nfce)}</span>
              <span className="label">NFC-e / Cupons</span>
            </div>
          </div>
        </KPICard>
        
        <KPICard className="itens">
          <div className="card-content">
            <div className="thumb"><FiBox /></div>
            <div className="text">
              <span className="value">{fmt(totals.itens)}</span>
              <span className="label">Itens / Produtos</span>
            </div>
          </div>
        </KPICard>
        
        <KPICard className="empresas">
          <div className="card-content">
            <div className="thumb"><FiBriefcase /></div>
            <div className="text">
              <span className="value">{uniqueCompanies}</span>
              <span className="label">Empresas</span>
            </div>
          </div>
        </KPICard>
      </KPIGrid>

      {/* Abas */}
      <div className="tabs-header">
        <button
          className={`tab ${activeTab === 'vinculados' ? 'active' : ''}`}
          onClick={() => setActiveTab('vinculados')}
        >
          <FiLink />
          Arquivos Vinculados
          <span className="tab-count">{vinculados.length}</span>
        </button>
        <button
          className={`tab ${activeTab === 'verificacao' ? 'active' : ''} ${naoVinculados.length > 0 ? 'warning' : ''}`}
          onClick={() => setActiveTab('verificacao')}
        >
          <FiAlertTriangle />
          Requer Verificação
          <span className="tab-count">{naoVinculados.length}</span>
        </button>
      </div>

      {/* Lista de Arquivos (card layout vertical) */}
      <FileTable>
        <div className="table-body">
          {currentFiles.length === 0 ? (
            <div className="empty-state">
              {activeTab === 'vinculados' ? (
                <>
                  <GoFile size={28} />
                  <p>Nenhum arquivo vinculado ao projeto</p>
                  <small>Os arquivos precisam ter a mesma raiz de CNPJ do projeto</small>
                </>
              ) : (
                <>
                  <GoCheck size={28} />
                  <p>Todos os arquivos estão vinculados!</p>
                  <small>Não há arquivos pendentes de verificação</small>
                </>
              )}
            </div>
          ) : (
            currentFiles.map((file, idx) => {
              const isVinculado = file.status === 'vinculado';
              return (
                <div key={idx} className={`table-row ${!isVinculado ? 'warning-row' : ''}`}>
                  <div className="cell">
                    <strong>{file.razaoSocial || 'Desconhecida'}</strong>
                    <small>{file.fileName}</small>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <small style={{ opacity: 0.5, fontSize: '0.72em' }}>{file.periodoInicio} a {file.periodoFim}</small>
                    {isVinculado ? (
                      <span className="status-badge valid"><GoCheck /> Vinculado</span>
                    ) : (
                      <span className="status-badge warning"><FiAlertCircle /> CNPJ Diferente</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '0.72em', opacity: 0.5 }}>
                    <span>NF-e: {fmt(file.totalNfe)}</span>
                    <span>CT-e: {fmt(file.totalCte)}</span>
                    <span style={{ fontFamily: 'monospace' }}>{file.cnpj || '-'}</span>
                  </div>
                  {!isVinculado && projectCnpjRoot && (
                    <small style={{ color: '#f59e0b', fontSize: '0.7em' }}>
                      Raiz esperada: {projectCnpjRoot.replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2.$3')}
                    </small>
                  )}
                </div>
              );
            })
          )}
        </div>
      </FileTable>

      {/* Aviso se houver arquivos não vinculados */}
      {naoVinculados.length > 0 && activeTab === 'vinculados' && (
        <div className="warning-banner">
          <FiAlertTriangle />
          <span>
            <strong>{naoVinculados.length} arquivo(s)</strong> não serão processados pois o CNPJ não corresponde ao projeto selecionado.
            <button onClick={() => setActiveTab('verificacao')}>Ver detalhes</button>
          </span>
        </div>
      )}
          </Container>
        </ModalBody>

        <ModalFooter $justify="space-between">
          <ModalButton className="secondary" onClick={onCancel}>
            <GoX /> Cancelar
          </ModalButton>
          <ModalButton 
            className="primary" 
            onClick={handleConfirm}
            disabled={!canProcess}
          >
            <GoRocket /> Processar {vinculados.length} Arquivo(s)
          </ModalButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ReviewScreen;
